import { useState, useContext,  useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ThemeContext } from "../../context/ThemeContext";
import api from "../../api/axios";
import config from "../../config/config";
import Toast from "../../components/common/Toast";
import { useTranslation } from "react-i18next";
import Logo from "../../components/logo/Logo";
import FloatingInput from "../../components/input/FloatingInput";
import { useCompanyAuthPath } from "../../hooks/useCompanyAuthPath";

// ✅ Localized validation schema
const schema = yup.object().shape({
  new_password: yup.string().required("reset_new_password_required"),
  confirm_password: yup
    .string()
    .oneOf([yup.ref("new_password"), null], "reset_passwords_must_match")
    .required("reset_confirm_password_required"),
});

export default function ResetPassword() {
  const { t: trans } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState({ visible: false, message: "", type: "success" });
  const [successMessage, setSuccessMessage] = useState("");
  const theme = useContext(ThemeContext);
  const [tokenStatus, setTokenStatus] = useState("pending"); // pending | valid | invalid
  const [tokenMessage, setTokenMessage] = useState("");
  const [token, setToken] = useState("");
  const navigate = useNavigate();
  const { companyPath } = useCompanyAuthPath();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    const tkn = new URLSearchParams(window.location.search).get("token") || "";
    setToken(tkn);
  }, []);

  useEffect(() => {
    if (!token) {
      setTokenStatus("invalid");
      setTokenMessage(trans("reset_invalid_token"));
      return;
    }

    const verifyToken = async () => {
      setTokenStatus("pending");
      try {
        const payload = { token, maincompanyid: theme.id };
        const res = await api.post("/auth/verify-reset-token", payload, { skipAuthRedirect: true });

        const ok = res?.data?.success || res?.data?.valid || res?.status === 200;
        if (ok) {
          setTokenStatus("valid");
        } else {
          setTokenStatus("invalid");
          setTokenMessage(res?.data?.message || trans("reset_invalid_title"));
        }       

      } catch (err) {
        const msg = err?.response?.data?.message || trans("reset_invalid_title");
        setTokenStatus("invalid");
        setTokenMessage(msg);
      }
    };

    verifyToken();
  }, [token, theme.id, trans]);

  const showToast = (message, type = "success", duration = 4000) => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast((t) => ({ ...t, visible: false })), duration);
  };

  const onSubmit = async (data) => {
    if (!token) {
      showToast(trans("reset_invalid_token"), "error");
      return;
    }
    setIsLoading(true);
    try {
      const payload = { new_password: data.new_password, token, maincompanyid: theme.id };
      const res = await api.post("/auth/reset-password", payload);
      if (res.data?.status === "success") {
        setSuccessMessage(res.data.message || trans("reset_success"));
        setTimeout(() => {
          window.location.href = companyPath("");
        }, 10000);
      } else {
        showToast(res.data?.message || trans("reset_something_wrong"), "error");
      }

       setTimeout(() => navigate(companyPath("")), 2200);
       
    } catch (err) {
      showToast(err?.response?.data?.message || trans("reset_failed"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="container-full bg-login"
      style={
        theme?.background_image
          ? { background: `url(${config.assetsBaseUrl}/${theme.background_image}) no-repeat center / cover` }
          : {}
      }
    >
      <div className="bg-layer">
        <div className="auth-container">
          <div className="auth-screens">
            <div className="company-logo">
              <Logo type="primary" size={196} />
            </div>

            <div className="auth-heading">
              <h2>{trans("reset_title")}</h2>

              {tokenStatus === "pending" && (
                <>
                  <p className="heading-d">{trans("reset_verifying")}</p>
                  <p className="heading-d">{trans("reset_wait_message")}</p>
                </>
              )}

              {tokenStatus === "invalid" && (
                <>
                  <p className="heading-d">{tokenMessage || trans("reset_invalid_title")}</p>
                  <p className="heading-d">{trans("reset_invalid_hint")}</p>
                  <button
                    type="button"
                    disabled={isLoading}
                    className="submit-btn btn-bg inpt-btn m-t-20 m-b-20-r"
                    onClick={() => (window.location.href = "/")}
                  >
                    {trans("reset_go_to_login")}
                  </button>
                </>
              )}

              {tokenStatus === "valid" && (
                <p className="text-sm text-gray-500 ft-12">{trans("reset_valid_hint")}</p>
              )}
            </div>

            {tokenStatus === "valid" && (
              <>
                <form onSubmit={handleSubmit(onSubmit)}>
                  {successMessage && (
                    <div className="p-3 text-sm rounded-lg bg-green-100 text-green-700 border border-green-300 mb-4">
                      {successMessage}
                    </div>
                  )}

                  <div className="input-field">
                    <div className="form-group">
                      <FloatingInput
                        type="password"
                        name="new_password"
                        label={trans("reset_new_password")}
                        className="inpt-pla"
                        {...register("new_password")}
                      />
                      {errors.new_password && (
                        <p className="field-error">{trans(errors.new_password.message)}</p>
                      )}
                    </div>

                    <div className="form-group">
                      <FloatingInput
                        type="password"
                        name="confirm_password"
                        label={trans("reset_confirm_password")}
                        className="inpt-pla"
                        {...register("confirm_password")}
                      />
                      {errors.confirm_password && (
                        <p className="field-error">{trans(errors.confirm_password.message)}</p>
                      )}
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="submit-btn btn-bg inpt-btn"
                  >
                    {isLoading ? trans("reset_button_changing") : trans("reset_button_change")}
                  </button>
                </form>

                <div className="auth-ftr">
                  <p className="heading-b">
                    {trans("reset_remembered")}{" "}
                    <Link to={companyPath("")} rel="noopener noreferrer" className="heading-b" >{trans("reset_back_to_login")}</Link>

                  </p>
                </div>
              </>
            )}
          </div>
        </div>
        <Toast message={toast.message} type={toast.type} visible={toast.visible} />
      </div>
      <Toast {...toast} />
    </div>
  );
}
